import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { RefreshCw, AlertCircle, CheckCircle, Info, Download, Database } from 'lucide-react';
import { Button } from '../ui/Button';
import toast from 'react-hot-toast';

interface ClientIDStats {
  total_clients: number;
  clients_with_id: number;
  clients_without_id: number;
}

interface NextClientID {
  value: number;
}

const ClientIDManager: React.FC = () => {
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const queryClient = useQueryClient();

  // Fetch client ID verification stats
  const { data: stats, isLoading: isStatsLoading, refetch: refetchStats } = useQuery({
    queryKey: ['clientIdStats'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('client_id_verification')
        .select('*')
        .single();
      
      if (error) throw error;
      return data as ClientIDStats;
    }
  });

  // Fetch next client ID
  const { data: nextIdData, isLoading: isNextIdLoading } = useQuery({
    queryKey: ['nextClientId'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('system_settings')
        .select('value')
        .eq('key', 'next_client_id')
        .single();
      
      if (error) throw error;
      return data.value as NextClientID;
    }
  });

  // Mutation to regenerate missing client IDs
  const regenerateClientIds = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase
        .rpc('regenerate_missing_client_ids');
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clientIdStats'] });
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      toast.success('Client IDs regenerated successfully');
    },
    onError: (error) => {
      toast.error('Failed to regenerate client IDs');
      console.error('Error regenerating client IDs:', error);
    }
  });

  // Mutation to update next client ID
  const updateNextClientId = useMutation({
    mutationFn: async (newValue: number) => {
      const { data, error } = await supabase
        .from('system_settings')
        .update({ value: { value: newValue } })
        .eq('key', 'next_client_id')
        .select();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['nextClientId'] });
      toast.success('Next client ID updated successfully');
    },
    onError: (error) => {
      toast.error('Failed to update next client ID');
      console.error('Error updating next client ID:', error);
    }
  });

  // Handle regenerate client IDs
  const handleRegenerateIds = async () => {
    setIsRegenerating(true);
    try {
      // First, check if there are any clients without IDs
      await refetchStats();
      
      if (stats && stats.clients_without_id > 0) {
        // Call the stored procedure to regenerate missing client IDs
        await regenerateClientIds.mutateAsync();
      } else {
        toast.success('All clients already have IDs');
      }
    } catch (error) {
      console.error('Error in regeneration process:', error);
      toast.error('Failed to complete regeneration process');
    } finally {
      setIsRegenerating(false);
    }
  };

  // Export client ID mapping
  const handleExportMapping = async () => {
    setIsExporting(true);
    try {
      const { data, error } = await supabase
        .from('clients')
        .select('id, client_id, first_name, last_name, email')
        .order('client_id', { ascending: true });
      
      if (error) throw error;
      
      // Create CSV content
      const headers = ['Database ID', 'Client ID', 'First Name', 'Last Name', 'Email'];
      const csvRows = [
        headers.join(','),
        ...data.map(client => [
          client.id,
          client.client_id,
          client.first_name,
          client.last_name,
          client.email
        ].join(','))
      ];
      
      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      
      // Create download link
      const a = document.createElement('a');
      a.href = url;
      a.download = `client_id_mapping_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success('Client ID mapping exported successfully');
    } catch (error) {
      console.error('Error exporting client ID mapping:', error);
      toast.error('Failed to export client ID mapping');
    } finally {
      setIsExporting(false);
    }
  };

  // Fetch audit logs for client ID assignments
  const { data: auditLogs, isLoading: isAuditLoading } = useQuery({
    queryKey: ['clientIdAuditLogs'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('audit_logs')
        .select('*')
        .in('action', ['assign_client_id', 'assign_client_id_migration'])
        .order('created_at', { ascending: false })
        .limit(50);
      
      if (error) throw error;
      return data;
    }
  });

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center gap-3 mb-4">
          <Database className="h-6 w-6 text-primary" />
          <h2 className="text-xl font-semibold">Client ID Management</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Total Clients</h3>
            <div className="text-2xl font-bold text-gray-900">
              {isStatsLoading ? (
                <div className="animate-pulse h-8 w-16 bg-gray-200 rounded"></div>
              ) : (
                stats?.total_clients || 0
              )}
            </div>
          </div>
          
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Clients With ID</h3>
            <div className="text-2xl font-bold text-green-600">
              {isStatsLoading ? (
                <div className="animate-pulse h-8 w-16 bg-gray-200 rounded"></div>
              ) : (
                stats?.clients_with_id || 0
              )}
            </div>
          </div>
          
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Clients Without ID</h3>
            <div className="text-2xl font-bold text-red-600">
              {isStatsLoading ? (
                <div className="animate-pulse h-8 w-16 bg-gray-200 rounded"></div>
              ) : (
                stats?.clients_without_id || 0
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="flex-1 bg-gray-50 rounded-lg p-4 border border-gray-200">
            <div className="flex justify-between items-center mb-2">
              <h3 className="text-sm font-medium text-gray-700">Next Client ID</h3>
              <span className="text-xs text-gray-500">Auto-incrementing</span>
            </div>
            <div className="text-2xl font-bold text-primary">
              {isNextIdLoading ? (
                <div className="animate-pulse h-8 w-24 bg-gray-200 rounded"></div>
              ) : (
                nextIdData?.value.toString().padStart(5, '0') || '10001'
              )}
            </div>
          </div>
          
          <div className="flex-1 bg-blue-50 rounded-lg p-4 border border-blue-200">
            <div className="flex items-start">
              <Info className="h-5 w-5 text-blue-500 mt-0.5 mr-2 flex-shrink-0" />
              <div>
                <h3 className="text-sm font-medium text-blue-800">Client ID Format</h3>
                <p className="mt-1 text-sm text-blue-700">
                  Client IDs are 5-digit sequential numbers starting from 10001, with leading zeros if necessary.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-4">
          <Button
            onClick={handleRegenerateIds}
            disabled={isRegenerating || (stats?.clients_without_id === 0)}
            className="flex items-center"
          >
            {isRegenerating ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Regenerating...
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4 mr-2" />
                Regenerate Missing IDs
              </>
            )}
          </Button>
          
          <Button
            onClick={handleExportMapping}
            disabled={isExporting}
            variant="outline"
            className="flex items-center"
          >
            {isExporting ? (
              <>
                <span className="loader mr-2"></span>
                Exporting...
              </>
            ) : (
              <>
                <Download className="h-4 w-4 mr-2" />
                Export ID Mapping
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Audit Logs */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h2 className="text-lg font-semibold mb-4">Client ID Assignment Audit Log</h2>
        
        {isAuditLoading ? (
          <div className="animate-pulse space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-12 bg-gray-200 rounded"></div>
            ))}
          </div>
        ) : auditLogs && auditLogs.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Timestamp
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Action
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Record ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Client ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {auditLogs.map((log) => (
                  <tr key={log.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(log.created_at).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        log.action === 'assign_client_id' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {log.action === 'assign_client_id' ? 'New Assignment' : 'Migration'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {log.record_id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {log.new_data?.client_id || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {log.user_id || 'System'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <p>No client ID assignment logs found</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientIDManager;